import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { getVendorStatus } from '../../services/subscriptionService';
import {
  useVendorAnalytics,
  useVendorPerformance,
  useVendorEarnings,
  useVendorInsights,
} from '../../services/vendorAnalyticsService';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import {
  BarChart3,
  Lock,
  ArrowRight,
  TrendingUp,
  DollarSign,
  Target,
  Lightbulb,
  CheckCircle,
  XCircle,
  Star,
  Users,
  Zap,
  Clock,
} from 'lucide-react';
import PlatformPerformanceCard from '../../components/vendor/PlatformPerformanceCard';

const COLORS = ['#2563EB', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const VendorAdvancedAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
  const [hasFeature, setHasFeature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (hasActiveSubscription === false) {
      navigate('/vendor/subscription', { replace: true });
      return;
    }
    if (hasActiveSubscription !== true) return;
    const fetchStatus = async () => {
      try {
        const response = await getVendorStatus();
        const features = response?.data?.current_subscription?.features || {};
        setHasFeature(!!features.feature_analytics);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const { data: analyticsData } = useVendorAnalytics(period);
  const { data: performanceData } = useVendorPerformance(period);
  const { data: earningsData } = useVendorEarnings(period);
  const { data: insightsData } = useVendorInsights(period);

  const analytics = analyticsData?.data || {};
  const performance = performanceData?.data || {};
  const earnings = earningsData?.data || {};
  const insights = insightsData?.data || {};

  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return formatter(value);
  };

  // Chart data transformations
  const revenueTrendData = (earnings.monthly_breakdown || analytics.booking_trends || []).map((item) => ({
    name: item.month || item.period,
    revenue: Number(item.net_earnings ?? item.gross_revenue ?? item.revenue ?? 0),
    bookings: Number(item.booking_count ?? 0),
  }));

  const earningsByServiceData = (earnings.earnings_by_service || []).map((s) => ({
    name: (s.service_name || '').slice(0, 20),
    value: Number(s.total_earnings || 0),
    fullName: s.service_name,
    bookings: s.bookings_count || 0,
  }));

  const performanceRadarData = [
    { metric: 'Platform score', value: Number(performance.platform_performance?.score || analytics.platform_performance?.score || 0), fullMark: 100 },
    { metric: 'Completion', value: Number(performance.completion_rate || 0), fullMark: 100 },
    { metric: 'Satisfaction', value: Number(performance.client_satisfaction || 0) * 20, fullMark: 100 },
    { metric: 'Repeat Clients', value: Number(performance.repeat_client_rate || 0), fullMark: 100 },
    { metric: 'Efficiency', value: Number(performance.service_efficiency || 0), fullMark: 100 },
    { metric: 'Low Cancel', value: 100 - Number(performance.cancellation_rate || 0), fullMark: 100 },
  ];

  const peakHoursData = (performance.peak_hours || []).map((h) => ({
    name: h.hour,
    bookings: h.bookings,
  }));

  const paymentMethodsData = (earnings.payment_methods || []).map((p, i) => ({
    name: (p.payment_method || 'Other').replace(/_/g, ' '),
    value: Number(p.total_amount || 0),
    color: COLORS[i % COLORS.length],
  })).filter((d) => d.value > 0);

  if (isChecking || loading || hasActiveSubscription !== true) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!hasFeature) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Advanced Analytics</h1>
          <p className="text-gray-600 mb-6">
            This feature is not included in your current plan. Upgrade to unlock charts, performance metrics, earnings breakdown, and AI-powered insights.
          </p>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 ">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Advanced Analytics</h1>
            <p className="text-sm text-gray-600">Charts, performance metrics, earnings breakdown & AI insights</p>
          </div>
          <Link
            to="/vendor/analytics"
            className="mt-4 sm:mt-0 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            ← Back to basic analytics
          </Link>
        </div>

        {/* Period Tabs */}
        <div className="flex space-x-4 border-b border-gray-200 mb-6 overflow-x-auto">
          {[
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'quarter', label: 'This Quarter' },
            { value: 'year', label: 'This Year' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setPeriod(tab.value)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                period === tab.value ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <PlatformPerformanceCard
          performance={performance.platform_performance || analytics.platform_performance}
        />

        {/* Summary KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Revenue</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {formatValue(earnings.total_earnings ?? analytics.total_revenue, (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net Earnings</p>
            <p className="text-xl font-bold text-green-600 mt-1">
              {formatValue(earnings.net_earnings, (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Completion Rate</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {formatValue(performance.completion_rate, (v) => `${Number(v).toFixed(1)}%`)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Client Rating</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {formatValue(performance.client_satisfaction ?? analytics.average_rating, (v) => `${Number(v).toFixed(1)}/5`)}
            </p>
          </div>
        </div>

        {/* Revenue & Bookings Trend Chart */}
        {revenueTrendData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Revenue & Bookings Over Time</h2>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={revenueTrendData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} tickFormatter={(v) => (v && v.length > 10 ? v.slice(0, 7) : v)} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => [name === 'revenue' ? `$${Number(value).toLocaleString()}` : value, name === 'revenue' ? 'Revenue' : 'Bookings']}
                    labelFormatter={(label) => (typeof label === 'string' ? label : String(label))}
                  />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#2563EB" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="bookings" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} name="Bookings" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Two-column charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Performance Radar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Performance Overview</h2>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={performanceRadarData}>
                  <PolarGrid stroke="#E5E7EB" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar name="Score" dataKey="value" stroke="#2563EB" fill="#2563EB" fillOpacity={0.4} strokeWidth={2} />
                  <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Score']} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Earnings by Service Bar Chart */}
          {earningsByServiceData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Earnings by Service</h2>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={earningsByServiceData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={75} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Earnings']} />
                    <Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} name="Earnings" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Peak Hours & Revenue by Payment Method - Same Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Peak Hours Bar Chart */}
          {peakHoursData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Peak Booking Hours</h2>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={peakHoursData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="bookings" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Bookings" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Revenue by Payment Method Pie Chart */}
          {paymentMethodsData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Revenue by Payment Method</h2>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethodsData}
                      cx="50%"
                      cy="50%"
                      // outerRadius={100}
                      dataKey="value"
                       innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      // dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {paymentMethodsData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, '']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Detailed Performance Metrics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Detailed Performance Metrics</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-gray-600">Completion Rate</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatValue(performance.completion_rate, (v) => `${Number(v).toFixed(1)}%`)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Completed vs confirmed bookings</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-gray-600">Cancellation Rate</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatValue(performance.cancellation_rate, (v) => `${Number(v).toFixed(1)}%`)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Cancelled vs total bookings</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-gray-600">Client Satisfaction</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatValue(performance.client_satisfaction, (v) => `${Number(v).toFixed(1)}/5`)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Average review rating</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-600">Repeat Client Rate</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatValue(performance.repeat_client_rate, (v) => `${Number(v).toFixed(1)}%`)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Clients with 2+ bookings</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Service Efficiency</span>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatValue(performance.service_efficiency, (v) => `${Number(v).toFixed(1)}%`)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Completed vs confirmed</p>
            </div>
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">Insights & Recommendations</h2>
          </div>
          {insights.key_insights && insights.key_insights.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Key Insights</p>
              <ul className="space-y-2">
                {insights.key_insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <Zap className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {insights.trends && insights.trends.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Trends</p>
              <div className="flex flex-wrap gap-2">
                {insights.trends.map((trend, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm ${
                      trend.direction === 'up' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <TrendingUp className={`w-4 h-4 ${trend.direction === 'down' ? 'rotate-180' : ''}`} />
                    {trend.metric}: {trend.change}
                  </span>
                ))}
              </div>
            </div>
          )}
          {insights.recommendations && insights.recommendations.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Recommendations</p>
              <div className="space-y-3">
                {insights.recommendations.map((rec, i) => (
                  <div key={i} className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="font-medium text-gray-900">{rec.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(!insights.key_insights || insights.key_insights.length === 0) &&
            (!insights.trends || insights.trends.length === 0) &&
            (!insights.recommendations || insights.recommendations.length === 0) && (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No insights available for this period yet</p>
                <p className="text-sm text-gray-500 mt-2">Keep getting bookings to unlock personalized insights</p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default VendorAdvancedAnalytics;
